import { useRef, useState } from "react";
import { Upload } from "lucide-react";
import { api, ApiError } from "../../lib/api";
import { toast } from "../../stores/toast";

interface SupportAttachmentUploadFieldProps {
  label: string;
  value: string;
  onChange: (storageKey: string) => void;
}

export function SupportAttachmentUploadField({ label, value, onChange }: SupportAttachmentUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  async function uploadFile(file: File) {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("files", file);
      const res = await api<{ keys: string[] }>("/uploads/private", { formData });
      const key = res.keys[0];
      if (!key) throw new Error("No file key returned");
      onChange(key);
      toast("success", "File uploaded");
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div className="space-y-2">
      {value ? (
        <p className="text-[11px] text-white/60">Attachment ready — tap replace to change.</p>
      ) : null}
      <button
        type="button"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
        className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-white/15 px-4 py-3 text-xs text-white/45 hover:border-brand-pink/40 disabled:opacity-50"
      >
        <Upload size={16} />
        {uploading ? "Uploading…" : value ? "Replace file" : `Upload ${label}`}
      </button>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept="image/*,.pdf,.doc,.docx"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void uploadFile(file);
        }}
      />
    </div>
  );
}
